import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
} from 'react';
import {
  GridCellModes,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowModesModel,
  GridSortModel,
  GridValidRowModel,
  GridValueFormatter,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import { getCaption } from '../../../../../../Model/BusinessModelUtils';
import {
  IClientAssociationModel,
  IClientBusinessModel,
} from '../../../../../../Types/ClientModelTypes';
import {
  addAllDataDependencies,
  findDisplayComp,
  findTableDefaultWidth,
  findTableMinWidth,
  IBusinessColorConfig,
  IBusinessListIconDef,
  IBusinessStylesConfig,
} from '../../../Const/BusinessConfigs';
import { ITableDef, TableEditModeContext } from './BusinessDataTable';
import {
  IAttributeInfo,
  safeGetAttributeInfo,
} from '../../../../../../Model/avenyModelUtils';
import { findFormatter } from '../../../../../../Utils/Formatters/FindFormatter';
import { useItemFunctionMenuItemsType } from '../../../../../../Utils/ObjektVyUtils';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import {
  append,
  dissoc,
  forEachObjIndexed,
  init,
  intersperse,
  last,
  path,
  prepend,
} from 'ramda';
import BusinessDataTableInfoCell from './Components/BusinessDataTableInfoCell';
import BusinessDetailContext from '../../BusinessDetailContext/BusinessDetailContext';
import { ISortProperty } from '../../../../../../BaseComponents/Table/Components/SortableTableUtils';
import BusinessDataTableMenuCell from './Components/BusinessDataTableMenuCell';
import LegendMenuButton from '../../../../../../Components/Legend/LegendMenuButton';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import { Attribut } from '../Attribut/Attribut';
import { EDDataGridClasses } from '../../../../../../BaseComponents/XGrid/EdDataGridUtils';
import { ObjektLank } from '../ObjektLank/ObjektLank';
import { IExtendedBusinessDataProps } from '../BusinessDataSet/BusinessDataSet';
import { asString } from '../../../../../../Utils/Formatters/Utils/ValueUtils';
import DisplayText from '../../../../../../Components/Attributes/DisplayText/DisplayText';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { SUMROWID } from './Consts/BusinessDataTableConsts';
import { BaseDisplayer } from '../../../../../../Utils/Formatters/Displayers/BaseDisplayer';
import { Result } from 'true-myth';
import classNames from 'classnames';
import { IBreakpointContext } from '../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import {
  MASKED_VALUE_TEXT,
  getMasked,
  getMaskedFlagFromCurrentValues,
} from '../../../Hooks/useAttributeValues';

const currFormatter = findFormatter('TEDFormatCurr');

export const rowsToDataGridRows = (
  columns: readonly IColumnDef[],
  rows: ICollectionDataType[],
  collection: IClientBusinessModel
): GridValidRowModel[] => {
  const attributeDefs = addAllDataDependencies(
    collection.CollectionName,
    columns
  );

  const rowsData = rows
    .filter(row => row?.id)
    .map(row => {
      let rowData: GridValidRowModel = {
        id: row.id,
        collectionData: row,
      };
      attributeDefs.forEach(attributeDef => {
        let pathArray = attributeDef.split('.');

        if (pathArray.length === 2) {
          pathArray = prepend('_Associations', pathArray);
        } else if (pathArray.length > 2) {
          pathArray = append(
            last(pathArray) || '',
            prepend(
              '_Associations',
              intersperse('_Associations', init(pathArray))
            )
          );
        }

        const value = path(pathArray, row);

        if (value || value === 0) {
          rowData[attributeDef] = value as string;
        }
      });

      return rowData;
    });
  return rowsData;
};

interface ICellElementProps {
  col: IColumnDef;
  params: GridRenderCellParams;
  rightAligned: boolean;
  displayer: BaseDisplayer | undefined;
  collection: IClientBusinessModel;
  editing: boolean;
  isCellDisabled?: (params: GridCellParams<any, any, any>) => boolean;
}

const CellElement: React.FC<
  React.PropsWithChildren<ICellElementProps>
> = props => {
  const { col, params, rightAligned, displayer, collection } = props;
  const tableEditMode = useContext(TableEditModeContext);
  const ref = React.useRef<HTMLInputElement>(undefined);
  const api = useGridApiContext();

  const cellDisabled = props.isCellDisabled?.(params);

  useLayoutEffect(() => {
    if (
      params.hasFocus &&
      ref.current &&
      document.activeElement !== ref.current &&
      tableEditMode.editMode === true
    ) {
      ref.current.focus();
      ref.current.select();
    }

    if (
      params.hasFocus &&
      api.current.getRowMode(params.id) === 'view' &&
      tableEditMode.editMode === true
    ) {
      api.current.startRowEditMode({ id: params.id });
    }
  }, [
    params.hasFocus,
    params.cellMode,
    tableEditMode,
    params.rowNode.id,
    api,
    params.id,
  ]);

  //när tableeditmode sätts till false så ska vi inte längre fokusera inputen
  useEffect(() => {
    if (
      ref.current &&
      document.activeElement === ref.current &&
      tableEditMode.editMode === false
    ) {
      ref.current.blur();
    }
  }, [tableEditMode]);

  // Applicera tabIndex imperativt på input-elementet istället för att skicka
  // disableTabbing som prop. Det gör att MemoBaseObjektLank inte behöver renderas
  // om enbart för att en cell tar/förlorar fokus, vilket annars bryter memon
  // när användaren klickar runt i griden.
  useLayoutEffect(() => {
    if (ref.current) {
      const disable =
        !tableEditMode.editMode && params.cellMode === GridCellModes.View;
      ref.current.tabIndex = disable ? -1 : 0;
    }
  }, [tableEditMode.editMode, params.cellMode]);

  const focusCell = useCallback(() => {
    if (!props.params.hasFocus) {
      forEachObjIndexed((_, key) => {
        if (props.params.id != key) {
          api.current.stopRowEditMode({ id: key, ignoreModifications: true });
        }
      }, api.current.state.editRows);
      api.current.setCellFocus(props.params.id, props.params.field);
    }
  }, [api, props.params.field, props.params.hasFocus, props.params.id]);

  if (params.row.id === SUMROWID) {
    return (
      <EDBox sx={{ marginRight: 1 }}>
        <DisplayText
          value={params.value ?? ''}
          displayer={displayer ?? currFormatter.displayer}
          bold
          lineHeight="inherit"
        />
      </EDBox>
    );
  } else if (col && col.association) {
    return (
      <ObjektLank
        collection={collection}
        associationName={col.association ?? ''}
        headerDefs={col.attributeDef ? col.attributeDef : undefined}
        hideLabel
        hideOutline
        allMargins="none"
        fullWidth
        validValues={col.customValidValues}
        lineMarker
        editMode={col.disableEditMode === true ? false : undefined}
        disableAssociationLink={col.disableAssociationLink}
        inputRef={ref}
        disabled={cellDisabled}
        disableDisabledBackgroundColor={cellDisabled}
        rowID={params.row.id}
        onFocus={focusCell}
      />
    );
  } else {
    return (
      <Attribut<{ [key: string]: any }>
        name={col.attributeDef}
        onlyValue
        fullWidth
        hideOutline
        formControlMargin="none"
        hideClearIcon={rightAligned}
        rightAlign={rightAligned}
        disableInputPaddingRight={rightAligned}
        rightPadding={rightAligned ? 4 : undefined}
        lineMarker
        disableEditMode={col.disableEditMode}
        inputRef={ref}
        disabled={cellDisabled}
        disableDisabledBackgroundColor={cellDisabled}
        rowID={params.row.id}
        onFocus={focusCell}
      />
    );
  }
};

const getCellElement = (props: ICellElementProps) => {
  return <CellElement {...props} />;
};

export const getColDefinitions = (
  columns: readonly IColumnDef[],
  collection: IClientBusinessModel,
  map: Map<string, IClientBusinessModel>,
  colors: IBusinessColorConfig[],
  getDefaultMinWidth: (field: string) => number | undefined,
  colIsRightAligned: (field: string) => 'right' | undefined,
  useFunktionsMenyItems: useItemFunctionMenuItemsType | undefined,
  hideDefaultFunktionsMenyItems: boolean | undefined,
  businessIcons: IBusinessListIconDef[],
  tableProps: IExtendedBusinessDataProps,
  tableEditable: boolean,
  association?: IClientAssociationModel,
  isCellDisabled?: (params: GridCellParams<any, any, any>) => boolean,
  breakpointContext?: IBreakpointContext
): ITableDef[] => {
  const headerColumns: ITableDef[] = [];
  const restColumns: ITableDef[] = [];
  columns.forEach(col => {
    const attributeDef = col.association
      ? `${col.association}.${col.attributeDef || 'IDString'}`
      : col.attributeDef;
    const attrInfo: Result<IAttributeInfo, string> = safeGetAttributeInfo(
      collection,
      attributeDef,
      map
    );

    const attr = attrInfo.map(i => i.attribute);

    const displayer = attr.isOk()
      ? findFormatter(attr.value.Formatter, attr.value.DataType).displayer
      : undefined;
    const valueFormatter: GridValueFormatter<any> = (value, row, column) => {
      const maskedFlag = getMaskedFlagFromCurrentValues(
        row?.collectionData,
        attributeDef
      );
      const masked = getMasked(
        attr.isOk() ? attr.value : undefined,
        maskedFlag
      );

      return masked
        ? MASKED_VALUE_TEXT
        : displayer && attr.isOk()
        ? displayer.display(value, true, attr.value.FormatterDisplayParams)
        : asString(value);
    };

    const DisplayComp = findDisplayComp(
      collection.CollectionName,
      col.attributeDef
    );

    // const minWidth =
    //   getDefaultMinWidth(col.attributeDef) ?? (tableProps.editable ? 175 : 120);
    const defaultWidth =
      col.attributeDef === 'InfoColumn'
        ? 36
        : attrInfo.isOk()
        ? findTableDefaultWidth(
            attrInfo.value.collection.CollectionName,
            attrInfo.value.attribute.AttributeName,
            breakpointContext?.localBreakPoint
          ) ?? 150
        : 150;
    const minWidth =
      col.attributeDef === 'InfoColumn'
        ? 36
        : attrInfo.isOk()
        ? findTableMinWidth(
            attrInfo.value.collection.CollectionName,
            attrInfo.value.attribute.AttributeName
          ) ?? 50
        : 50;

    const columnEditable = tableEditable && !col.disableEditMode;

    const align = colIsRightAligned(attributeDef);
    const rightAligned = align === 'right';

    if (col.header) {
      headerColumns.push({
        field: attributeDef,
        editable: false,
        headerName: getCaption(col, collection, map),
        minWidth,
        width: defaultWidth,
        // flex: attributeValueSizeToFlex(
        //   getColumnSize(map, collection.CollectionName, col)
        // ),
        // header: true,
        align: align,
        renderCell: columnEditable
          ? (params: GridRenderCellParams) => {
              return getCellElement({
                col,
                params,
                rightAligned,
                displayer,
                collection,
                editing: false,
                isCellDisabled,
              });
            }
          : DisplayComp
          ? params => (
              <BusinessDetailContext.Provider
                value={{ data: params.row.collectionData }}
              >
                <DisplayComp
                  value={params.value}
                  displayValue={params.formattedValue}
                />
              </BusinessDetailContext.Provider>
            )
          : undefined,
        headerAlign: align,
        hideable: false,
        disableColumnMenu: true,
        valueFormatter: valueFormatter,
        cellClassName: params =>
          classNames(
            EDDataGridClasses.linkText,
            EDDataGridClasses.cellReadonly,
            {
              [EDDataGridClasses.cellDisabled]:
                isCellDisabled && isCellDisabled(params),
            }
          ),
        disableReorder: true,
      });
    } else {
      restColumns.push({
        field: attributeDef,
        editable: columnEditable,
        headerName: getCaption(col, collection, map),
        renderCell: columnEditable
          ? (params: GridRenderCellParams) => {
              return getCellElement({
                col,
                params,
                rightAligned,
                displayer,
                collection,
                editing: false,
                isCellDisabled,
              });
            }
          : DisplayComp
          ? params => (
              <BusinessDetailContext.Provider
                value={{ data: params.row.collectionData }}
              >
                <DisplayComp
                  value={params.value}
                  displayValue={params.formattedValue}
                />
              </BusinessDetailContext.Provider>
            )
          : undefined,

        renderEditCell: (params: GridRenderCellParams) => {
          return getCellElement({
            col,
            params,
            rightAligned,
            displayer,
            collection,
            editing: true,
            isCellDisabled,
          });
        },
        minWidth: minWidth,
        width: defaultWidth,
        align: align,
        headerAlign: align,
        hideable: false,
        disableColumnMenu: true,
        valueFormatter: valueFormatter,
        cellClassName: params =>
          classNames({
            [EDDataGridClasses.cellDisabled]:
              isCellDisabled && isCellDisabled(params),
            [EDDataGridClasses.cellReadonly]: col.disableEditMode,
          }),
      });
    }
  });
  const infoColumn: GridColDef = {
    field: 'InfoColumn',
    resizable: false,
    headerName: 'Färgmarkeringar',
    renderCell: params => (
      <BusinessDataTableInfoCell row={params.row} colors={colors} />
    ),
    renderHeader: () => <LegendMenuButton posts={colors} />,
    width: 55,
    hideable: false,
    disableColumnMenu: true,
    disableReorder: true,
    sortable: false,
    cellClassName: params =>
      classNames(EDDataGridClasses.linkText, {
        [EDDataGridClasses.cellDisabled]:
          isCellDisabled && isCellDisabled(params),
        [EDDataGridClasses.menuColumnCellInEditableTable]: tableEditable,
      }),
  };
  const menuColumn: ITableDef = {
    field: 'MenuColumn',
    resizable: false,
    headerName: 'Funktioner',
    description: 'Funktioner',
    editable: false,
    renderHeader: params => <EDIcon iconType="build" size="small" />,
    renderCell: (params: GridRenderCellParams) => {
      const disabled = Boolean(isCellDisabled?.(params));

      return params.row.id === SUMROWID ? null : (
        <BusinessDataTableMenuCell
          useFunktionsMenyItems={useFunktionsMenyItems}
          hideDefaultFunktionsMenyItems={hideDefaultFunktionsMenyItems}
          collection={collection}
          associationName={association?.AssociationName}
          disabled={disabled}
          businessIcons={businessIcons}
          renderCellParams={params}
          funtionMenuClassName={EDDataGridClasses.functionMenu}
          row={params.row}
          tableEditable={tableEditable}
        />
      );
    },
    renderEditCell: params => {
      const disabled = Boolean(isCellDisabled?.(params));
      return params.row.id === SUMROWID ? null : (
        <BusinessDataTableMenuCell
          useFunktionsMenyItems={useFunktionsMenyItems}
          hideDefaultFunktionsMenyItems={hideDefaultFunktionsMenyItems}
          collection={collection}
          associationName={association?.AssociationName}
          disabled={disabled}
          businessIcons={businessIcons}
          renderCellParams={params}
          funtionMenuClassName={EDDataGridClasses.functionMenu}
          row={params.row}
          tableEditable={tableEditable}
        />
      );
    },
    width: 60 + businessIcons.length * 40,
    flex: 0,
    align: 'right',
    headerAlign: 'right',
    hideable: false,
    disableColumnMenu: true,
    disableReorder: true,
    sortable: false,
    cellClassName: params =>
      classNames(EDDataGridClasses.linkText, {
        [EDDataGridClasses.cellDisabled]:
          isCellDisabled && isCellDisabled(params),
        [EDDataGridClasses.menuColumnCellInEditableTable]: tableEditable,
      }),
  };
  const finalColumns = tableHasInfoColumn(
    colors,
    tableProps.collectionStylesConfig
  )
    ? [...headerColumns, infoColumn, ...restColumns, menuColumn]
    : [...headerColumns, ...restColumns, menuColumn];
  return finalColumns;
};

export const tableHasInfoColumn = (
  colors: IBusinessColorConfig[],
  collectionStylesConfig: IBusinessStylesConfig | undefined
): boolean => {
  const hideLegendColum = collectionStylesConfig?.hideLegendColumn;

  return colors.length > 0 && !hideLegendColum;
};

export const sortPropertiesToSortModel = (
  sortProperties: ISortProperty[]
): GridSortModel =>
  sortProperties.map(sortProperty => ({
    field: sortProperty.orderBy,
    sort: sortProperty.order,
  }));

export const sortModelToSortProperties = (
  sortModel: GridSortModel,
  columns: readonly IColumnDef[]
): ISortProperty[] =>
  sortModel.map(modelEntry => ({
    order: modelEntry.sort ? modelEntry.sort : undefined,
    orderBy: modelEntry.field,
    caption:
      columns.find(col => col.attributeDef === modelEntry.field)?.caption ?? '',
  }));

export const removeDeletedRowsFromRowModesModel = (
  rowModesModel: GridRowModesModel,
  rows: GridValidRowModel[]
): GridRowModesModel => {
  const rowKeysToRemove = Object.keys(rowModesModel).filter(
    key => !rows.some(row => row.id === key)
  );
  if (rowKeysToRemove.length > 0) {
    return rowKeysToRemove.reduce(
      (prevRowModesModel, deletedRowKey) =>
        dissoc(deletedRowKey, prevRowModesModel),
      rowModesModel
    );
  } else {
    return rowModesModel;
  }
};
